'use client'
import { useState } from 'react'
import { useRouter } from 'next/navigation'
import type { FormEventHandler } from 'react'
import { useTranslation } from 'react-i18next'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { TextWithLink } from '@/components/TextWithLink'
import { useShopSessionId } from '@/services/shopSession/ShopSessionContext'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { useRedeemCampaign } from '@/utils/useCampaign'
import { Button } from 'ui/src/components/Button/Button'
import { TextField } from 'ui/src/components/TextField/TextField'
import { yStack } from 'ui/src/patterns/stack'
import { usePrintTextEffect } from './usePrintTextEffect'
import { layout } from './ForeverPage.css'

export type Props = { initialCode: string }

export const ForeverPage = ({ initialCode }: Props) => {
  const router = useRouter()
  const { t } = useTranslation()

  const [code, setCode] = useState('')
  usePrintTextEffect({ value: initialCode, onValueChange: setCode })

  const shopSessionId = useShopSessionId()
  const locale = useRoutingLocale()
  const redirectUrl = PageLink.store({ locale })
  const [addCampaign, { errorMessage, loading, called }] = useRedeemCampaign({
    shopSessionId: shopSessionId ?? '',
    onCompleted() {
      router.push(redirectUrl.toString())
    },
  })
  const showLoading = loading || (called && !errorMessage)

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    router.prefetch(redirectUrl.pathname)
    addCampaign(code)
  }

  return (
    <main className={layout}>
      <GridLayout.Root>
        <GridLayout.Content width="1/3" align="center">
          <form onSubmit={handleSubmit}>
            <div className={yStack({ gap: 'xxs' })}>
              <TextField
                name="code"
                label={t('FOREVER_PAGE_INPUT_TEXT')}
                value={code}
                onValueChange={(value) => setCode(value)}
                required={true}
                message={errorMessage}
                warning={!!errorMessage}
                upperCaseInput={true}
              />
              <Button type="submit" size="large" loading={showLoading} fullWidth={true}>
                {t('FOREVER_PAGE_BTN_LABEL')}
              </Button>
            </div>
          </form>
        </GridLayout.Content>
      </GridLayout.Root>
      <GridLayout.Root>
        <GridLayout.Content width="1/3" align="center">
          <TextWithLink as="p" size="xs" align="center" balance={true} href={PageLink.home({ locale }).pathname}>
            {t('FOREVER_PAGE_FOOTER_TEXT')}
          </TextWithLink>
        </GridLayout.Content>
      </GridLayout.Root>
    </main>
  )
}
